import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GraduationCap, Sprout, Trees, Stethoscope, Search, CheckCircle, Zap, Layers, TrendingUp, Target, Brain, MapPin } from 'lucide-react'

interface Solution {
  id: string
  industry: string
  title: string
  subtitle: string
  icon: any
  color: string
  painPoints: string[]
  solution: string
  features: string[]
  scenarios: string[]
  advantages: string[]
}

const solutionsData: Solution[] = [
  {
    id: 'edu',
    industry: '基础教育',
    title: '基础教育解决方案',
    subtitle: 'AI 赋能教育数字化转型',
    icon: GraduationCap,
    color: 'from-blue-500 to-cyan-500',
    painPoints: [
      '校园安全隐患难以及时发现',
      '传统管理方式效率低下',
      '教育资源分配不均衡',
      '数字化建设水平参差不齐',
    ],
    solution: '静守™校园安全管控系统 + AI 基础教育核心产品方案，构建智慧教育新生态',
    features: [
      '校园安全智能预警',
      '教学管理数字化',
      '教育资源云平台',
      '数据分析与决策支持',
      '家校协同育人',
      '个性化学习推荐',
    ],
    scenarios: [
      '校园安全防控',
      '教学管理优化',
      '教育数字化转型',
      '素质教育评价',
    ],
    advantages: [
      '99%+ 安全事件识别率',
      '分钟级预警响应',
      '全流程数据追溯',
      '隐私保护设计',
    ],
  },
  {
    id: 'agriculture',
    industry: '智慧农田',
    title: '农业（智慧农田）解决方案',
    subtitle: '无人机+AI 驱动的智慧农田管理系统',
    icon: Sprout,
    color: 'from-green-500 to-emerald-500',
    painPoints: [
      '农田管理人力成本高',
      '植保作业效率低',
      '农药化肥使用不精准',
      '作物长势监测困难',
    ],
    solution: '高精度航测与三维建模、智能作业规划、变量精准施药一体化解决方案',
    features: [
      '无人机高精度航测',
      '农田三维建模',
      '智能作业路径规划',
      '变量精准施药',
      '作物长势监测',
      '产量预估分析',
    ],
    scenarios: [
      '规模化农田管理',
      '山地果园植保',
      '农作物长势监测',
      '产量预估与决策',
    ],
    advantages: [
      '作业效率提升 10 倍+',
      '农药使用量减少 30%',
      '厘米级定位精度',
      '全自动智能作业',
    ],
  },
  {
    id: 'forestry',
    industry: '智慧林草',
    title: '林业（智慧林草）解决方案',
    subtitle: '林草资源智能监测与管理',
    icon: Trees,
    color: 'from-emerald-500 to-teal-500',
    painPoints: [
      '林区面积大巡查困难',
      '火情发现不及时',
      '病虫害监测滞后',
      '资源调查工作量大',
    ],
    solution: '空天地一体化林草资源智能监测体系，实现森林防火、病虫害监测、资源调查全覆盖',
    features: [
      '卫星遥感监测',
      '无人机巡护',
      '地面传感器网络',
      '火情自动识别',
      '病虫害预警',
      '资源动态调查',
    ],
    scenarios: [
      '森林防火监控',
      '病虫害监测防治',
      '林草资源调查',
      '生态保护监管',
    ],
    advantages: [
      '24 小时不间断监测',
      '火情识别准确率 95%+',
      '覆盖面积广',
      '数据实时更新',
    ],
  },
  {
    id: 'animal-husbandry',
    industry: '智慧畜牧',
    title: '畜牧业解决方案',
    subtitle: '智慧畜牧业大数据 AI 管理平台',
    icon: Sprout,
    color: 'from-orange-500 to-amber-500',
    painPoints: [
      '养殖管理粗放',
      '疫病监测不及时',
      '产品质量难追溯',
      '生产效率低下',
    ],
    solution: '基于物联网和 AI 技术的智慧畜牧大数据管理平台，实现养殖全过程智能化',
    features: [
      '个体身份识别',
      '健康状况监测',
      '精准饲喂管理',
      '疫病预警诊断',
      '质量追溯体系',
      '生产数据分析',
    ],
    scenarios: [
      '畜牧养殖管理',
      '疫病监测防控',
      '产品质量追溯',
      '精细化饲养',
    ],
    advantages: [
      '养殖效率提升 40%',
      '疫病发生率降低 60%',
      '全程可追溯',
      '科学决策支持',
    ],
  },
  {
    id: 'hr-social-security',
    industry: '人社服务',
    title: '人社（社保一卡通）解决方案',
    subtitle: '社保服务数字化转型',
    icon: Trees,
    color: 'from-purple-500 to-pink-500',
    painPoints: [
      '社保卡应用场景有限',
      '就业服务效率不高',
      '人才管理手段落后',
      '数据共享不充分',
    ],
    solution: '以社保卡为载体的数字化服务体系，整合就业、人才、社保等多领域服务',
    features: [
      '社保卡多场景应用',
      '就业服务一站式',
      '人才信息管理',
      '大数据分析决策',
      '移动服务平台',
      '跨部门数据共享',
    ],
    scenarios: [
      '社保卡综合服务',
      '就业创业服务',
      '人才引进管理',
      '职业技能培训',
    ],
    advantages: [
      '一卡多用便民',
      '办事效率提升 80%',
      '数据互联互通',
      '服务全天候',
    ],
  },
  {
    id: 'medical',
    industry: '智慧医疗',
    title: '医疗（智慧医疗）解决方案',
    subtitle: '基于阿里云通义千问大模型的 AI 智慧医疗中枢',
    icon: Stethoscope,
    color: 'from-red-500 to-rose-500',
    painPoints: [
      '医疗文书工作量大',
      '分诊效率有待提升',
      '临床决策支持不足',
      '急救响应速度慢',
    ],
    solution: '基于阿里云通义千问大模型的 AI 智慧医疗中枢，赋能医疗服务全流程',
    features: [
      '医疗文本自动生成',
      'AI 导诊预诊',
      '临床决策辅助 (CDSS)',
      '智慧 120 急救',
      '健康证智能管理',
      '医学影像辅助诊断',
    ],
    scenarios: [
      '门诊住院文书自动化',
      'AI 辅助分诊',
      '院前急救协同',
      '公共卫生监管',
    ],
    advantages: [
      '文书效率提升 70%',
      '分诊准确率 95%+',
      '急救响应提速 50%',
      '大模型专业赋能',
    ],
  },
  {
    id: 'emergency',
    industry: '应急管理',
    title: '应急管理解决方案',
    subtitle: '智能化应急指挥与管理',
    icon: Stethoscope,
    color: 'from-yellow-500 to-orange-500',
    painPoints: [
      '突发事件响应慢',
      '应急预案执行难',
      '救援指挥协调复杂',
      '信息传递不及时',
    ],
    solution: '智能化应急指挥平台，实现突发事件快速响应、科学处置、高效救援',
    features: [
      '突发事件监测预警',
      '应急预案数字化',
      '救援指挥调度',
      '多方协同联动',
      '资源智能调配',
      '态势分析研判',
    ],
    scenarios: [
      '突发事件处置',
      '应急预案管理',
      '救援指挥调度',
      '防灾减灾救灾',
    ],
    advantages: [
      '响应时间缩短 60%',
      '指挥效率提升 80%',
      '资源优化配置',
      '全程可视化',
    ],
  },
]

const SolutionsPage = () => {
  const [activeCategory, setActiveCategory] = useState<string>('全部')
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null)

  const categories = ['全部', '基础教育', '智慧农田', '智慧林草', '智慧畜牧', '人社服务', '智慧医疗', '应急管理']

  const filteredSolutions = activeCategory === '全部'
    ? solutionsData
    : solutionsData.filter(s => s.industry === activeCategory || 
        (activeCategory === '智慧农田' && s.id === 'agriculture') ||
        (activeCategory === '智慧林草' && s.id === 'forestry') ||
        (activeCategory === '智慧畜牧' && s.id === 'animal-husbandry') ||
        (activeCategory === '人社服务' && s.id === 'hr-social-security') ||
        (activeCategory === '智慧医疗' && s.id === 'medical') ||
        (activeCategory === '应急管理' && s.id === 'emergency'))

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="section-title">行业解决方案</h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto mb-8">
              深耕七大行业领域，提供专业化、智能化的数字化转型解决方案
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {categories.map((category) => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveCategory(category)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-gradient-to-r from-tech-500 to-tech-400 text-white shadow-lg shadow-tech-500/30'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {category}
                </motion.button>
              ))}
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredSolutions.map((solution, index) => (
                <SolutionCard
                  key={solution.id}
                  solution={solution}
                  index={index}
                  onClick={() => setSelectedSolution(solution)}
                />
              ))}
            </AnimatePresence>
          </div>

          {filteredSolutions.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Search className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/50">暂无解决方案</p>
            </motion.div>
          )}
        </div>
      </section>

      {selectedSolution && (
        <SolutionModal
          solution={selectedSolution}
          onClose={() => setSelectedSolution(null)}
        />
      )}
    </div>
  )
}

const SolutionCard = ({ solution, index, onClick }: { solution: Solution; index: number; onClick: () => void }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      whileHover={{ y: -8, scale: 1.02 }}
      onClick={onClick}
      className="tech-card cursor-pointer group"
    >
      <div className="p-6">
        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${solution.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
          <solution.icon className="w-8 h-8 text-white" />
        </div>

        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-tech-400 transition-colors">
          {solution.title}
        </h3>

        <p className="text-white/60 text-sm mb-4">
          {solution.subtitle}
        </p>

        <div className="space-y-2 mb-4">
          {solution.scenarios.slice(0, 3).map((scenario, i) => (
            <div key={i} className="flex items-center text-white/50 text-sm">
              <CheckCircle className="w-4 h-4 mr-2 text-tech-400" />
              {scenario}
            </div>
          ))}
        </div>

        <div className="flex items-center text-tech-400 text-sm font-medium">
          <span>查看详情</span>
          <Zap className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </motion.div>
  )
}

const SolutionModal = ({ solution, onClose }: { solution: Solution; onClose: () => void }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 50 }}
        className="bg-gradient-to-br from-deep-blue to-deep-navy border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gradient-to-r from-deep-blue to-deep-navy border-b border-white/10 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${solution.color} flex items-center justify-center`}>
              <solution.icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold gradient-text">{solution.title}</h2>
              <p className="text-white/60 text-sm">{solution.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-tech-400" />
              行业痛点
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {solution.painPoints.map((point, i) => (
                <div key={i} className="flex items-start space-x-2 bg-white/5 rounded-lg p-3">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                  <span className="text-white/70 text-sm">{point}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Layers className="w-5 h-5 mr-2 text-tech-400" />
              解决方案
            </h3>
            <div className="bg-gradient-to-r from-tech-500/10 to-cyan-500/10 border border-tech-500/20 rounded-xl p-4">
              <p className="text-white/80 leading-relaxed">{solution.solution}</p>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Brain className="w-5 h-5 mr-2 text-tech-400" />
              核心功能
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {solution.features.map((feature, i) => (
                <div key={i} className="flex items-center space-x-2 bg-white/5 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-tech-400 flex-shrink-0" />
                  <span className="text-white/70 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-tech-400" />
              应用场景
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {solution.scenarios.map((scenario, i) => (
                <div key={i} className="flex items-center space-x-2 bg-white/5 rounded-lg p-3">
                  <MapPin className="w-4 h-4 text-tech-400 flex-shrink-0" />
                  <span className="text-white/70 text-sm">{scenario}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-tech-400" />
              产品优势
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {solution.advantages.map((advantage, i) => (
                <div key={i} className="flex items-center space-x-2 bg-gradient-to-r from-tech-500/10 to-transparent rounded-lg p-3 border border-tech-500/20">
                  <Zap className="w-5 h-5 text-tech-400 flex-shrink-0" />
                  <span className="text-white font-medium text-sm">{advantage}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-gradient-to-r from-deep-blue to-deep-navy border-t border-white/10 p-6">
          <button
            onClick={onClose}
            className="w-full tech-button py-3"
          >
            关闭
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default SolutionsPage
